// Tunable constants, gathered in one place so the configurable knobs have one source.

export const CONFIG = {
  /** Seconds of song visible above the hit line. */
  lookaheadSeconds: 3,
  /** Per-frame delta cap (s): a backgrounded tab refocuses with a multi-second RAF gap;
   *  clamping keeps that from sweeping a wall of misses + a metronome click burst in one step. */
  maxFrameDt: 0.1,
  /** Notes whose onsets fall within this window are played as one chord. */
  chordWindowMs: 40,
  /** Hand-split (floating split-point) tunables. */
  handSplit: {
    /** Seed split pitch when track structure can't seed it (middle C). */
    seedMidi: 60,
    /** Fraction the split moves toward each slice's gap midpoint. */
    alpha: 0.34,
    /** Max semitones the split may move per slice (anti-flap). */
    maxMoveSemitones: 4,
    /** Minimum L↔R gap (semitones) before the split is allowed to move. */
    minGapSemitones: 2,
  },
  /** Display-only note release gap: each note's drawn tail (and the demo's key
   *  lighting) ends this much early so repeated same-pitch notes visibly
   *  re-press instead of merging into one bar. Capped at a fraction of the note
   *  so fast passages don't collapse. Never touches the model or scoring. */
  noteGapMs: 70,
  noteGapMaxFraction: 0.3,
  /** Soft-fade the trailing (release) edge of a falling note over this many px
   *  (capped at half the note), so the note's end reads as a tail, not a hard cut. */
  noteFadePx: 18,
  /** Scored mode: max |timing error| (ms) that still counts as a hit. */
  hitWindowMs: 120,
  /** Scored mode: inner window (ms) that counts as a "perfect" hit. */
  perfectWindowMs: 50,
  /** Key highlighting: how long before a note's onset its key glows blue (anticipation). */
  keyLeadMs: 400,
  /** Key highlighting: a release within this of the note's end isn't "early" (legato gap). */
  keyReleaseMarginMs: 120,
  /** Keyboard band height is proportional to white-key width (piano-like), so
   *  narrow mobile keys aren't drawn as tall bars — clamped to [min, max] and to
   *  a fraction of canvas height so it never dominates a short landscape viewport. */
  keyboardHeightPx: 150, // max band height (tall desktop screens)
  keyboardMinHeightPx: 70,
  keyboardKeyAspect: 4.5, // band height ≈ whiteKeyWidth × this, before clamping
  keyboardMaxHeightFraction: 0.25,
  /** Default white-key width in px (overridden by calibration). */
  defaultWhiteWidthPx: 38,
  /** Black-key width as a fraction of a white key. */
  blackKeyWidthRatio: 0.62,
  /** Black-key height as a fraction of the keyboard strip. */
  blackKeyHeightRatio: 0.62,
  /** Lowest / highest MIDI note on an 88-key piano (A0..C8). */
  lowMidi: 21,
  highMidi: 108,
  /** Covered-range origin: centered on middle C by default. */
  originMidi: 60,
  /** Schematic sight-reading staff (render/staff.ts). */
  staff: {
    /** Bars per wrapped system in Page mode. */
    barsPerSystem: 4,
    /** Flow mode: now-line x as a fraction of staff width (room to read ahead). */
    nowLineFraction: 0.3,
    /** Flow mode: seconds of music visible ahead of the now-line. */
    flowLookaheadSeconds: 6,
    /** Future-note proximity ramp: full-bright within `proxNear`s of now, `faintAlpha` at/beyond `proxFar`s. */
    proxNear: 0.5,
    proxFar: 6,
    faintAlpha: 0.3,
    /** Miss red-edge blink lasts this many seconds of song time past the onset. */
    missBlinkSeconds: 0.5,
    /** A wrong-note red notehead fades out over this many seconds. */
    wrongFadeSeconds: 0.6,
    /** Max recent wrong presses retained for the staff overlay. */
    wrongRingSize: 16,
    /** Pixels between adjacent staff lines (a line+space pair = one diatonic third). */
    lineGapPx: 9,
    /** Vertical gap (px) between the treble and bass staves within a system. */
    staffGapPx: 36,
    /** Staff occupies at most this fraction of canvas width (centered); sheet-music-like. */
    maxWidthFraction: 0.5,
    /** Vertical breathing room (px) above and below the grand staff within its band. */
    padPx: 14,
    /** Padding gap (px) below the staff band before the highway (or the keyboard, when
     *  the staff fills the space) — so the staff never abuts the falling notes / keys. */
    belowStaffGapPx: 24,
    /** Note-value glyph geometry (× lineGap unless noted). */
    stemLenGaps: 3.3,
    flagGapGaps: 0.55,
    flagLenGaps: 1.1,
    dotRadiusPx: 1.7,
    dotOffsetPx: 5,
    beamWidthPx: 3,
    beamSpanGaps: 0.5,
    /** Beam slant: fraction of the outer-note rise to apply, capped at this many
     *  line-gaps total — gentle, contour-following, never a steep diagonal. */
    beamSlantDampen: 0.45,
    beamMaxSlantGaps: 1.4,
    /** Flow mode: diatonic step above middle C (C4=0) placed at the band's vertical
     *  center — biases the view onto the melody (treble) instead of the empty bass. */
    flowCenterStep: 2,
    /** Split presets: [highway, staff] fractions of the area above the keyboard. */
    splitPresets: [
      [1, 0],
      [0.7, 0.3],
      [0.5, 0.5],
      [0.3, 0.7],
      [0, 1],
    ],
  },
  /** MIDI-out playback / accompaniment (F1). Notes the player doesn't own are sent to
   *  the keyboard's own synth — no in-app synth. One channel, the keyboard's current voice. */
  midiOut: {
    /** Output channel (0-based). */
    channel: 0,
    /** Velocity floor (1–127) so a near-silent note still sounds; generated content is
     *  already full-velocity, so this only lifts genuinely-soft notes in real files. */
    velFloor: 1,
    /** All-notes-off / all-sound-off CC numbers, sent on panic so nothing hangs. */
    allNotesOffCc: 123,
    allSoundOffCc: 120,
  },
  /** First-run / demo selection. */
  firstRun: {
    /** Hardcoded simple starter (falls back to the simplest catalog piece if absent).
     *  The Five-finger-in-C drill: the gentlest thing in the catalog (C major, no black
     *  keys, single notes, one hand) — so a beginner who connects a keyboard and enters
     *  Coach lands on the easiest curriculum drill, not a real two-handed piece (the
     *  Menuet was way too hard a starting point). A warmer one-hand folk-tune default is
     *  the F5 corpus-gap item in the roadmap. */
    defaultSongId: "drill-five-finger-c-g1",
    /** Demo reel draws from pieces at or above this RCM level (intermediate band). */
    demoMinLevel: 3,
    /** Fixed seed for the demo reel RNG (no Math.random; reproducible). */
    demoSeed: 0x1f2e3d,
    /** White keys the demo keyboard fits to the canvas width (~36 keys incl. black,
     *  3 octaves) — a fixed window so the keys never rescale when the reel loops. */
    demoVisibleWhiteKeys: 21,
  },
} as const;

export const COLORS = {
  bg: "#0b0c0f",
  guide: "#1b1f27",
  hitLine: "#3a4150",
  noteRight: "#4c8dff",
  noteLeft: "#27c8a6",
  noteUnknown: "#9aa3b2",
  whiteKey: "#e6e8ec",
  whiteKeyEdge: "#b9bdc6",
  blackKey: "#23262d",
  blackKeyEdge: "#0b0c0f",
  keyExpected: "#f4c542",
  keyCorrect: "#3ddc84",
  keyWrong: "#ff5a5a",
  /** A key physically held down, shown when not actively gating (MIDI feedback). */
  keyPressed: "#6cc5ff",
  noteHit: "#3ddc84",
  noteMiss: "#6b3a3a",
  hud: "#cfd4dd",
  staffLine: "#2a2f3a",
  staffClef: "#9aa3b2",
  /** Should-be-played target (reuses the right-hand blue). */
  staffTarget: "#4c8dff",
  /** Correctly played (= noteHit green). */
  staffHit: "#3ddc84",
  /** Wrong note / miss-blink edge (= keyWrong red). */
  staffWrong: "#ff5a5a",
  /** Faded history / passed-unplayed grey. */
  staffHistory: "#5b626f",
} as const;
